import React, { useState, useRef } from 'react';
import axiosAdmin from '../services/axiosAdmin';
import { API_URL } from '../services/api';

const DEFAULT_CATEGORIES = [
    { id: 'item0', label: 'Item 0 - Practice (Tools)', min: 10, max: 10 },
    { id: 'item1', label: 'Item 1 - Fruits', min: 10, max: 10 },
    { id: 'item2', label: 'Item 2 - Vegetables', min: 10, max: 10 },
    { id: 'item3', label: 'Item 3 - Sports', min: 10, max: 10 },
    { id: 'item4', label: 'Item 4 - Clothes', min: 10, max: 10 },
    { id: 'item5', label: 'Item 5 - Kitchen', min: 10, max: 10 },
    { id: 'item6', label: 'Item 6 - Household', min: 10, max: 10 },
    { id: 'item7', label: 'Item 7 - Animals', min: 10, max: 10 },
    { id: 'item8', label: 'Item 8 - Transport', min: 10, max: 10 }
];

export default function HerPherElements({ elements, loadElements, showToast, gameKey = 'working_memory_herpher', categories = DEFAULT_CATEGORIES, strict = false }) {
    const CATEGORIES = categories;
    const [uploading, setUploading] = useState(null);
    const fileRefs = useRef({});

    const SERVER_BASE = API_URL.replace(/\/api$/, '');

    const getImageUrl = (element) => {
        if (element.file_path.startsWith('/assets')) {
            return element.file_path;
        }
        return `${SERVER_BASE}${element.file_path}`;
    };

    // Resize image via HTML5 Canvas
    const resizeImage = (file, maxWidth, maxHeight) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = maxWidth;
                canvas.height = maxHeight;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#FFFFFF'; // White background just in case
                ctx.fillRect(0, 0, maxWidth, maxHeight);
                
                // Draw image scaled to fit and centered, but the spec says EXACTLY 300x300.
                // We'll just draw it stretched to 300x300, or cover it.
                // Cover behavior:
                const scale = Math.max(maxWidth / img.width, maxHeight / img.height);
                const x = (maxWidth / 2) - (img.width / 2) * scale;
                const y = (maxHeight / 2) - (img.height / 2) * scale;
                
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

                canvas.toBlob((blob) => {
                    const resizedFile = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
                    resolve(resizedFile);
                }, 'image/jpeg', 0.95);
            };
            img.src = URL.createObjectURL(file);
        });
    };

    const handleFileSelect = async (category, file, replaceId = null) => {
        if (!file) return;

        setUploading(replaceId ? `replace_${replaceId}` : `upload_${category}`);

        try {
            // Resize image to 300x300
            const resizedFile = await resizeImage(file, 300, 300);

            const formData = new FormData();
            formData.append('file', resizedFile);
            formData.append('test_id', gameKey);
            formData.append('asset_type', category);
            formData.append('language', 'all'); // Her Pher images are language independent
            
            if (replaceId) {
                formData.append('replace_id', replaceId);
            }

            const res = await axiosAdmin.post('/admin/elements/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                showToast(replaceId ? 'Image replaced successfully' : 'Image uploaded successfully');
                loadElements(gameKey);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            showToast('Failed to upload image', 'error');
        } finally {
            setUploading(null);
            if (fileRefs.current[category]) fileRefs.current[category].value = '';
            if (replaceId && fileRefs.current[`replace_${replaceId}`]) fileRefs.current[`replace_${replaceId}`].value = '';
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const res = await axiosAdmin.put(`/admin/elements/${id}/status`);
            if (res.data.success) {
                showToast('Status updated successfully');
                loadElements(gameKey);
            }
        } catch (error) {
            console.error('Toggle failed:', error);
            showToast('Failed to update status', 'error');
        }
    };

    return (
        <div className="elements-section">
            <h3>Her Pher Game Items</h3>
            <p className="elements-desc">Manage images used for the Her Pher memory game. Images will be automatically resized to 300x300.</p>
            
            {CATEGORIES.map(category => {
                // Get all elements for this category
                const categoryElements = elements.filter(el => el.asset_type === category.id);
                // Strict (V3) categories have a fixed required count — only the
                // ACTIVE images count toward it, matching what the game itself
                // actually draws from (see elementsController.js's matching
                // server-side enforcement).
                const activeCount = categoryElements.filter(el => el.is_active).length;
                const currentCount = strict ? activeCount : categoryElements.length;
                const isComplete = currentCount === category.max;

                return (
                    <div key={category.id} className="hp-category-block" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ margin: 0 }}>{category.label}</h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <span style={{ fontSize: '0.85rem', color: isComplete ? '#10b981' : '#ef4444', fontWeight: strict ? 700 : 400 }}>
                                    {strict
                                        ? `${currentCount} / ${category.max} images — ${isComplete ? 'Complete' : 'Incomplete'}`
                                        : `${currentCount} / ${category.max} items`}
                                </span>
                                {/* Strict (V3) categories have a fixed image count that's only
                                    ever provisioned by the initial seed — there's no "add a new
                                    one" case, only Replace on an existing image, so this control
                                    doesn't apply here at all (matches the backend's matching
                                    rejection in elementsController.js). */}
                                {!strict && (
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            ref={el => fileRefs.current[category.id] = el}
                                            onChange={(e) => handleFileSelect(category.id, e.target.files[0])}
                                        />
                                        <button
                                            className="admin-btn admin-btn-primary"
                                            onClick={() => fileRefs.current[category.id]?.click()}
                                            disabled={uploading === `upload_${category.id}` || currentCount >= category.max}
                                        >
                                            {uploading === `upload_${category.id}` ? 'Uploading...' : 'Add Image'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="elements-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem' }}>
                            {categoryElements.map(el => (
                                <div key={el.id} className="element-card" style={{ padding: '0.5rem', opacity: el.is_active ? 1 : 0.5 }}>
                                    <div className="element-preview" style={{ height: '120px', marginBottom: '0.5rem' }}>
                                        <img 
                                            src={getImageUrl(el)} 
                                            alt={el.file_name} 
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    </div>
                                    <div className="element-meta" style={{ fontSize: '0.75rem', marginBottom: '0.5rem', wordBreak: 'break-all', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{el.file_name}</span>
                                        <span style={{ color: el.is_active ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                                            {el.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="element-actions" style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            style={{ display: 'none' }}
                                            ref={input => fileRefs.current[`replace_${el.id}`] = input}
                                            onChange={(e) => handleFileSelect(category.id, e.target.files[0], el.id)}
                                        />
                                        <button 
                                            className="admin-btn admin-btn-secondary" 
                                            style={{ flex: 1, padding: '0.25rem', fontSize: '0.75rem' }}
                                            onClick={() => fileRefs.current[`replace_${el.id}`]?.click()}
                                            disabled={uploading === `replace_${el.id}`}
                                        >
                                            Replace
                                        </button>
                                        {/* Strict (V3) categories must always hold exactly the required
                                            number of ACTIVE images — deactivating one would always break
                                            that with no way to recover from this panel, so the toggle is
                                            only offered here to re-activate a rare inactive leftover, never
                                            to deactivate a currently-active image. Use Replace instead. */}
                                        {(!strict || !el.is_active) && (
                                            <button
                                                className={`admin-btn ${el.is_active ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                                                style={{ flex: 1, padding: '0.25rem', fontSize: '0.75rem' }}
                                                onClick={() => handleToggleStatus(el.id)}
                                            >
                                                {el.is_active ? 'Deactivate' : 'Activate'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
